import { useEffect, useMemo, useState } from 'react';
import cdoAI from '../cdo/cdoAI.js';
import cdoBeing from '../cdo/cdoBeing.js';
import cdoEvent from '../cdo/cdoEvent.js';
import cdoPlace from '../cdo/cdoPlace.js';

const NPC_RESPONSE_INTENTS = new Set([
  'speak',
  'persuade',
  'deceive',
  'intimidate',
  'barter',
  'trade',
  'command',
  'hide',
  'sneak',
]);

const emptyNpcSelection = {
  pc: '',
  primaryFocus: '',
  nearbyOne: '',
  nearbyTwo: '',
};

const emptyNpcDetails = {
  pc: null,
  primaryFocus: null,
  nearbyOne: null,
  nearbyTwo: null,
};

const AI_QUOTA_ERROR_MESSAGE = 'AI token balance is empty. Deposit more AI tokens into your account and try again.';
const AI_RATE_LIMIT_ERROR_MESSAGE = 'AI request limit reached. Wait a moment and try again.';

const collectErrorText = (value, seen = new Set()) => {
  if (value === null || value === undefined) {
    return [];
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return [String(value)];
  }

  if (typeof value !== 'object') {
    return [];
  }

  if (seen.has(value)) {
    return [];
  }
  seen.add(value);

  const priorityFields = [
    'message',
    'sqlMessage',
    'error',
    'code',
    'status',
    'statusText',
    'rawBody',
    'apiResponse',
  ];
  const parts = priorityFields.flatMap((field) => collectErrorText(value[field], seen));

  try {
    parts.push(JSON.stringify(value));
  } catch {
    // Ignore non-serializable error details.
  }

  return parts.filter(Boolean);
};

const getFriendlyAiErrorMessage = (source) => {
  const errorText = collectErrorText(source).join(' ');
  if (!errorText) {
    return '';
  }

  if (/exceeded your current quota|insufficient_quota|billing details/i.test(errorText)) {
    return AI_QUOTA_ERROR_MESSAGE;
  }

  if (/429 Too Many Requests|request failed \(429|rate.?limit/i.test(errorText)) {
    return AI_RATE_LIMIT_ERROR_MESSAGE;
  }

  return '';
};

const formatErrorMessage = (err, response) => {
  const source = response?.error ?? response ?? err;
  const friendlyAiError = getFriendlyAiErrorMessage(source);
  if (friendlyAiError) {
    return friendlyAiError;
  }

  if (typeof source === 'string') {
    return source;
  }

  if (typeof source === 'number') {
    return `Request failed with status ${source}.`;
  }

  if (!source || typeof source !== 'object') {
    return 'Request failed.';
  }

  return source.message || source.sqlMessage || source.error || JSON.stringify(source);
};

const callCdo = (fn, ...args) =>
  new Promise((resolve, reject) => {
    fn(...args, (err, response) => {
      if (err || response?.error) {
        reject(new Error(formatErrorMessage(err, response)));
        return;
      }

      resolve(response);
    });
  });

const toArray = (value) => (Array.isArray(value) ? value : []);

const getEntityLabel = (entity) => {
  if (!entity) {
    return '';
  }

  const name = entity.name || entity.entityName || '';
  const title = entity.title || entity.entityTitle || '';
  if (name && title) {
    return `${name} (${title})`;
  }

  return name || title || entity.idEntity || '';
};

const getItemLabel = (item) => getEntityLabel(item);

const getSocialFrameTarget = (being) => {
  if (!being) {
    return null;
  }

  return {
    idEntity: being.idEntity || '',
    name: being.name || being.entityName || '',
    title: being.title || being.entityTitle || '',
  };
};

const normalizeResultIntent = (intent) => {
  if (typeof intent === 'string') {
    return {
      intent,
      perceiveSubAction: null,
    };
  }

  return {
    intent: intent?.intent || '',
    perceiveSubAction: intent?.perceiveSubAction ?? null,
  };
};

const getResultIntents = (result) =>
  (Array.isArray(result?.intents)
    ? result.intents
    : result?.primaryIntent
      ? [result.primaryIntent]
      : []
  ).map(normalizeResultIntent).filter((intent) => intent.intent);

const getNpcResponseIntents = (result) => {
  const seen = new Set();
  const responseIntents = [];

  for (const intent of getResultIntents(result)) {
    if (!NPC_RESPONSE_INTENTS.has(intent.intent) || seen.has(intent.intent)) {
      continue;
    }

    seen.add(intent.intent);
    responseIntents.push(intent.intent);
  }

  return responseIntents;
};

const getResultSocialContext = (result) =>
  Array.isArray(result?.socialContext) ? result.socialContext : [];

const getResultTargetEntityIds = (result) =>
  toArray(result?.targetEntityIds).filter(Boolean);

const findPrimaryFocusWorkplaceId = (being) => {
  const relationship = toArray(being?.relationships).find((entry) =>
    String(entry?.direction ?? '').toLowerCase() === 'outgoing' &&
    String(entry?.relateType ?? '').toLowerCase() === 'works at' &&
    String(entry?.otherEntityType ?? '').toLowerCase() === 'place' &&
    entry?.otherEntityId
  );

  return relationship?.otherEntityId || '';
};

const uniqueEvents = (events) => {
  const seen = new Set();
  const unique = [];

  for (const event of toArray(events)) {
    const key = event?.idEntity || event?.idEvent || JSON.stringify(event);
    if (!key || seen.has(key)) {
      continue;
    }

    seen.add(key);
    unique.push(event);
  }

  return unique;
};

function PlayerTextForm({
  formId,
  label = 'Player Text',
  text,
  setText,
  isSubmitting,
  submitLabel = 'Submit',
  onSubmit,
  onClear,
}) {
  const handleTextKeyDown = (event) => {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent?.isComposing) {
      return;
    }

    event.preventDefault();
    if (!isSubmitting && text.trim()) {
      event.currentTarget.form?.requestSubmit();
    }
  };

  return (
    <form className="generator-form" onSubmit={onSubmit}>
      <label htmlFor={formId} className="editor-field">
        <span className="field-label">{label}</span>
        <textarea
          id={formId}
          className="text-area editor-textarea gameplay-play-textarea"
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={handleTextKeyDown}
          disabled={isSubmitting}
        />
      </label>
      <div className="button-row">
        <button type="submit" className="button-with-icon" disabled={isSubmitting || !text.trim()}>
          <span>{isSubmitting ? 'Submitting...' : submitLabel}</span>
        </button>
        <button type="button" className="secondary-button" onClick={onClear} disabled={isSubmitting}>
          Clear
        </button>
      </div>
    </form>
  );
}

function IntentResultList({ result, entityNameById = new Map(), emptyLabel = 'No result yet.' }) {
  const resultIntents = getResultIntents(result);
  const resultSocialContext = getResultSocialContext(result);
  const targetEntityIds = getResultTargetEntityIds(result);

  if (resultIntents.length === 0) {
    return <div className="empty-grid">{emptyLabel}</div>;
  }

  return (
    <div className="gameplay-play-intent-list">
      {resultIntents.map((intent) => (
        <div
          key={`${intent.intent}:${intent.perceiveSubAction || ''}`}
          className="gameplay-play-intent-result"
        >
          {intent.intent}
          {intent.perceiveSubAction ? `: ${intent.perceiveSubAction}` : ''}
        </div>
      ))}
      {resultSocialContext.length > 0 && (
        <div className="gameplay-play-context-list">
          {resultSocialContext.map((context) => (
            <span key={context}>{context}</span>
          ))}
        </div>
      )}
      {targetEntityIds.length > 0 && (
        <div className="gameplay-play-context-list">
          {targetEntityIds.map((idEntity) => (
            <span key={idEntity} title={idEntity}>
              Target: {entityNameById.get(idEntity) || idEntity}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function BeingSelect({ id, label, value, options, disabled, onChange }) {
  return (
    <label htmlFor={id} className="editor-field">
      <span className="field-label">{label}</span>
      <select
        id={id}
        className="select-input"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">Select Being</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function NpcResponseResults({ groups, entityNameById, itemNameById }) {
  if (!Array.isArray(groups) || groups.length === 0) {
    return <div className="empty-grid">No NPC response yet.</div>;
  }

  return (
    <div className="gameplay-play-npc-response-list">
      {groups.map((group) => {
        const responses = toArray(group?.result?.responses);
        return (
          <div key={group.intent} className="gameplay-play-npc-response-group">
            <h5>{group.intent}</h5>
            <NpcResponseDebug debug={group?.result?.debug} entityNameById={entityNameById} itemNameById={itemNameById} />
            {responses.length > 0 ? (
              responses.map((response, index) => {
                const responderLabel = entityNameById.get(response.idEntity) || response.idEntity;
                const responseIntents = toArray(response.intents)
                  .map(normalizeResultIntent)
                  .filter((intent) => intent.intent);
                return (
                  <div
                    key={`${group.intent}:${response.idEntity}:${index}`}
                    className="gameplay-play-npc-response"
                  >
                    <div className="gameplay-play-npc-response-heading">
                      <strong title={response.idEntity}>{responderLabel}</strong>
                      <div className="gameplay-play-context-list">
                        {responseIntents.map((intent) => (
                          <span key={`${intent.intent}:${intent.perceiveSubAction || ''}`}>
                            {intent.intent}
                            {intent.perceiveSubAction ? `: ${intent.perceiveSubAction}` : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                    {response.verbalResponse && (
                      <div className="gameplay-play-npc-verbal-response">{response.verbalResponse}</div>
                    )}
                    {toArray(response.effects).some((effect) => formatEffectLabel(effect)) && (
                      <div className="gameplay-play-context-list">
                        {toArray(response.effects)
                          .map((effect, effectIndex) => ({
                            key: `${effect.type || 'effect'}:${effect.targetIdEntity || response.idEntity}:${effectIndex}`,
                            label: formatEffectLabel(effect),
                          }))
                          .filter((effect) => effect.label)
                          .map((effect) => (
                            <span key={effect.key}>{effect.label}</span>
                          ))}
                      </div>
                    )}
                    {(response.selling_for_price || response.buying_for_price) && (
                      <div className="gameplay-play-context-list">
                        {response.selling_for_price && (
                          <span>Selling: {formatStructuredPrice(response.selling_for_price)}</span>
                        )}
                        {response.buying_for_price && (
                          <span>Buying: {formatStructuredPrice(response.buying_for_price)}</span>
                        )}
                      </div>
                    )}
                    {(toArray(response.selling_items).length > 0 || toArray(response.buying_items).length > 0) && (
                      <div className="gameplay-play-context-list">
                        {toArray(response.selling_items).map((itemId) => (
                          <span key={`selling:${itemId}`} title={itemId}>
                            Selling item: {itemNameById.get(itemId) || itemId}
                          </span>
                        ))}
                        {toArray(response.buying_items).map((itemId) => (
                          <span key={`buying:${itemId}`} title={itemId}>
                            Buying item: {itemNameById.get(itemId) || itemId}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="empty-grid">No response returned for this intent.</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function formatSignedNumber(value) {
  const numericValue = Number(value) || 0;
  return numericValue > 0 ? `+${numericValue}` : String(numericValue);
}

function formatRollParts(rolls) {
  const parts = toArray(rolls)
    .map((roll) => {
      const sign = Number(roll?.sign) < 0 ? '-' : '+';
      return `${sign}${Number(roll?.roll) || 0}`;
    })
    .join(' ');

  return parts.replace(/^\+/, '') || '0';
}

function hasDisplayValue(value) {
  return value !== null && value !== undefined && value !== '';
}

function formatItemMeasurements(item) {
  const parts = [];
  const hasLength = hasDisplayValue(item?.length);
  const hasWeight = hasDisplayValue(item?.weight);
  const hasHeight = hasDisplayValue(item?.height);
  const hasWidth = hasDisplayValue(item?.width);
  const hasVolume = hasDisplayValue(item?.volume);
  const hasCapacity = hasDisplayValue(item?.capacity);

  if (hasLength) {
    parts.push(`length ${item.length}`);
  }
  if (hasWeight) {
    parts.push(`weight ${item.weight}`);
  }
  if (hasHeight) {
    parts.push(`height ${item.height}`);
  }
  if (hasWidth) {
    parts.push(`width ${item.width}`);
  }
  if (hasVolume || hasCapacity) {
    parts.push(`volume ${hasVolume ? item.volume : 0}${hasCapacity ? `/${item.capacity}` : ''}`);
  }

  return parts.length > 0 ? `, ${parts.join(', ')}` : '';
}

function formatDebugItemValue(item) {
  const value = item?.baseValueGp;
  if (item?.valueBasis === 'gold_per_pound') {
    return `${value} gold/lb value`;
  }

  return `${value} gold value`;
}

const priceDenominations = [
  'mithril',
  'platinum',
  'gold',
  'steel',
  'silver',
  'nickle',
  'iron',
  'copper',
  'bronze',
];

function formatStructuredPrice(price) {
  if (!price || typeof price !== 'object') {
    return '';
  }

  const parts = priceDenominations
    .map((denomination) => {
      const amount = Number(price[denomination]) || 0;
      return amount > 0 ? `${amount} ${denomination}` : '';
    })
    .filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : '0';
}

function formatRounds(rounds) {
  const numericRounds = Number(rounds) || 0;
  return `${numericRounds} round${numericRounds === 1 ? '' : 's'}`;
}

function formatEffectLabel(effect) {
  if (!effect || effect.type !== 'fear' || !effect.applied) {
    return '';
  }

  return `Fear: ${formatRounds(effect.rounds)}`;
}

function NpcResponseDebug({ debug, entityNameById, itemNameById }) {
  const barter = debug?.barter;
  const command = debug?.command;
  const intimidate = debug?.intimidate;
  const stealth = debug?.hide || debug?.sneak;
  const stealthLabel = debug?.sneak ? 'Sneak' : 'Hide';

  if (command) {
    const pcLabel = entityNameById.get(command.pc?.idEntity) || command.pc?.name || command.pc?.idEntity || 'PC';

    return (
      <div className="gameplay-play-debug-panel">
        <div className="gameplay-play-debug-grid">
          <div>
            <strong>{pcLabel}</strong>
            <span>PC roll: {command.pc?.dieTotal} ({formatRollParts(command.pc?.rolls)})</span>
            <span>Charisma: {formatSignedNumber(command.pc?.charisma)}</span>
            <span>Social: {command.pc?.socialLevel} / {formatSignedNumber(command.pc?.socialModifier)}</span>
            <span>PC base total: {command.pc?.baseTotal}</span>
          </div>
          {toArray(command.responders).map((responder) => {
            const responderLabel = entityNameById.get(responder.idEntity) || responder.name || responder.idEntity || 'NPC';
            return (
              <div key={responder.idEntity}>
                <strong>{responderLabel}</strong>
                <span>Rapport: {responder.rapportScore} / {formatSignedNumber(responder.rapportModifier)}</span>
                <span>
                  Alignment: {responder.alignmentConflict?.severity || 'unknown'} / {formatSignedNumber(responder.alignmentModifier)}
                </span>
              </div>
            );
          })}
        </div>
        {toArray(command.responders).some((responder) => responder.alignmentConflict?.severity && responder.alignmentConflict.severity !== 'none') && (
          <div className="gameplay-play-debug-quotes">
            {toArray(command.responders)
              .filter((responder) => responder.alignmentConflict?.severity && responder.alignmentConflict.severity !== 'none')
              .map((responder) => {
                const responderLabel = entityNameById.get(responder.idEntity) || responder.name || responder.idEntity || 'NPC';
                const conflict = responder.alignmentConflict;
                return (
                  <span key={`${responder.idEntity}:alignment`}>
                    {responderLabel}: {conflict.pcAlignment || 'unknown'} vs {conflict.npcAlignment || 'unknown'} - {String(conflict.responseDirection || '').replace(/_/g, ' ')}
                  </span>
                );
              })}
          </div>
        )}
      </div>
    );
  }

  if (intimidate) {
    const actorLabel = entityNameById.get(intimidate.actor?.idEntity) ||
      intimidate.actor?.name ||
      intimidate.actor?.idEntity ||
      'Actor';

    return (
      <div className="gameplay-play-debug-panel">
        <div className="gameplay-play-debug-grid">
          <div>
            <strong>{actorLabel}</strong>
            <span>Intimidate roll: {intimidate.actor?.dieTotal} ({formatRollParts(intimidate.actor?.rolls)})</span>
            <span>Charisma: {formatSignedNumber(intimidate.actor?.charisma)}</span>
            <span>Social: {intimidate.actor?.socialLevel} / {formatSignedNumber(intimidate.actor?.socialModifier)}</span>
            <span>Total: {intimidate.actor?.total}</span>
          </div>
          {toArray(intimidate.targets).map((target) => {
            const targetLabel = entityNameById.get(target.idEntity) || target.name || target.idEntity || 'Target';
            return (
              <div key={target.idEntity}>
                <strong>{targetLabel}</strong>
                <span>Combat resist: {target.dieTotal} ({formatRollParts(target.rolls)})</span>
                <span>Level: {target.level} / {formatSignedNumber(target.levelModifier)}</span>
                <span>Level gap penalty: {formatSignedNumber(target.levelGapPenalty)}</span>
                <span>Adjusted resist: {target.resistanceTotal}</span>
                <span>Difference: {formatSignedNumber(target.difference)}</span>
                <span>Fear: {target.feared ? formatRounds(target.fearRounds) : 'no'}</span>
                <span>Rapport: {target.rapportScore} / {formatSignedNumber(target.rapportModifier)}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (!barter) {
    if (!stealth) {
      return null;
    }

    const pcLabel = entityNameById.get(stealth.pc?.idEntity) || stealth.pc?.name || stealth.pc?.idEntity || 'PC';

    return (
      <div className="gameplay-play-debug-panel">
        <div className="gameplay-play-debug-grid">
          <div>
            <strong>{pcLabel}</strong>
            <span>{stealthLabel} roll: {stealth.pc?.dieTotal} ({formatRollParts(stealth.pc?.rolls)})</span>
            <span>Agility: {formatSignedNumber(stealth.pc?.statModifier)}</span>
            <span>Stealth: {stealth.pc?.skillLevel} / {formatSignedNumber(stealth.pc?.skillModifier)}</span>
            <span>Total: {stealth.pc?.total}</span>
          </div>
          {toArray(stealth.observers).map((observer) => {
            const observerLabel = entityNameById.get(observer.idEntity) || observer.name || observer.idEntity || 'NPC';
            return (
              <div key={observer.idEntity}>
                <strong>{observerLabel}</strong>
                <span>Perception roll: {observer.dieTotal} ({formatRollParts(observer.rolls)})</span>
                <span>Intellect: {formatSignedNumber(observer.statModifier)}</span>
                <span>Level: {observer.level} / {formatSignedNumber(observer.levelModifier)}</span>
                <span>Total: {observer.total}</span>
                <span>Detected: {observer.perceived ? 'yes' : 'no'}</span>
                <span>Rapport: {observer.rapportScore} / {formatSignedNumber(observer.rapportModifier)}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const pcLabel = entityNameById.get(barter.pc?.idEntity) || barter.pc?.name || barter.pc?.idEntity || 'PC';
  const npcLabel = entityNameById.get(barter.npc?.idEntity) || barter.npc?.name || barter.npc?.idEntity || 'NPC';

  return (
    <div className="gameplay-play-debug-panel">
      <div className="gameplay-play-debug-grid">
        <div>
          <strong>{pcLabel}</strong>
          <span>Roll: {barter.pc?.dieTotal} ({formatRollParts(barter.pc?.rolls)})</span>
          <span>Charisma: {formatSignedNumber(barter.pc?.charisma)}</span>
          <span>Social: {barter.pc?.socialLevel} / {formatSignedNumber(barter.pc?.socialModifier)}</span>
          <span>Rapport: {barter.pc?.rapportScore} / {formatSignedNumber(barter.pc?.rapportModifier)}</span>
          <span>Total: {barter.pc?.total}</span>
        </div>
        <div>
          <strong>{npcLabel}</strong>
          <span>Roll: {barter.npc?.dieTotal} ({formatRollParts(barter.npc?.rolls)})</span>
          <span>Charisma: {formatSignedNumber(barter.npc?.charisma)}</span>
          <span>Social: {barter.npc?.socialLevel} / {formatSignedNumber(barter.npc?.socialModifier)}</span>
          <span>Total: {barter.npc?.total}</span>
        </div>
        <div>
          <strong>Quote Guidance</strong>
          <span>Difference: {formatSignedNumber(barter.difference)}</span>
          <span>PC adjustment: {formatSignedNumber(barter.barterAdjustmentPercent)}%</span>
          <span>Base markup: {barter.baseMarkupPercent}%</span>
          <span>AI makes quote</span>
        </div>
      </div>
      {toArray(barter.quoteCandidateItems).length > 0 && (
        <div className="gameplay-play-debug-quotes">
          {barter.quoteCandidateItems.map((item) => (
            <span key={item.idEntity} title={item.idEntity}>
              {itemNameById.get(item.idEntity) || item.name || item.idEntity}: {formatDebugItemValue(item)}{formatItemMeasurements(item)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function GameplayPlayTestPanel() {
  const [intentText, setIntentText] = useState('');
  const [intentResult, setIntentResult] = useState(null);
  const [intentError, setIntentError] = useState('');
  const [isSubmittingIntent, setIsSubmittingIntent] = useState(false);

  const [beingOptions, setBeingOptions] = useState([]);
  const [isLoadingBeings, setIsLoadingBeings] = useState(false);
  const [beingLoadError, setBeingLoadError] = useState('');
  const [npcSelection, setNpcSelection] = useState(emptyNpcSelection);
  const [npcDetails, setNpcDetails] = useState(emptyNpcDetails);
  const [isLoadingNpcContext, setIsLoadingNpcContext] = useState(false);
  const [npcContextError, setNpcContextError] = useState('');
  const [primaryFocusWorkplace, setPrimaryFocusWorkplace] = useState(null);
  const [nearbyWorkplaces, setNearbyWorkplaces] = useState([]);
  const [isLoadingWorkplace, setIsLoadingWorkplace] = useState(false);
  const [isLoadingNearbyWorkplaces, setIsLoadingNearbyWorkplaces] = useState(false);
  const [npcText, setNpcText] = useState('');
  const [npcIntentResult, setNpcIntentResult] = useState(null);
  const [npcResponseGroups, setNpcResponseGroups] = useState([]);
  const [npcError, setNpcError] = useState('');
  const [isSubmittingNpc, setIsSubmittingNpc] = useState(false);

  const beingSelectOptions = useMemo(
    () =>
      beingOptions
        .map((being) => ({
          value: being.idEntity,
          label: getEntityLabel(being),
        }))
        .filter((option) => option.value)
        .sort((left, right) => left.label.localeCompare(right.label)),
    [beingOptions]
  );

  const primaryFocusWorkplaceId = useMemo(
    () => findPrimaryFocusWorkplaceId(npcDetails.primaryFocus),
    [npcDetails.primaryFocus]
  );
  const nearbyWorkplaceIds = useMemo(
    () => [...new Set([
      findPrimaryFocusWorkplaceId(npcDetails.nearbyOne),
      findPrimaryFocusWorkplaceId(npcDetails.nearbyTwo),
    ].filter(Boolean))],
    [npcDetails.nearbyOne, npcDetails.nearbyTwo]
  );
  const entityNameById = useMemo(() => {
    const nameById = new Map();
    [npcDetails.pc, npcDetails.primaryFocus, npcDetails.nearbyOne, npcDetails.nearbyTwo]
      .filter(Boolean)
      .forEach((being) => {
        if (being.idEntity) {
          nameById.set(being.idEntity, getEntityLabel(being));
        }
      });
    return nameById;
  }, [npcDetails]);
  const itemNameById = useMemo(() => {
    const nameById = new Map();
    [
      ...toArray(npcDetails.pc?.ownedItems),
      ...toArray(npcDetails.primaryFocus?.ownedItems),
      ...toArray(npcDetails.nearbyOne?.ownedItems),
      ...toArray(npcDetails.nearbyTwo?.ownedItems),
      ...toArray(primaryFocusWorkplace?.containedItems),
      ...nearbyWorkplaces.flatMap((workplace) => toArray(workplace?.containedItems)),
    ].forEach((item) => {
      if (item?.idEntity) {
        nameById.set(item.idEntity, getItemLabel(item));
      }
    });
    return nameById;
  }, [nearbyWorkplaces, npcDetails, primaryFocusWorkplace]);

  useEffect(() => {
    let isCancelled = false;

    setIsLoadingBeings(true);
    setBeingLoadError('');
    callCdo(cdoBeing.selectAll.bind(cdoBeing))
      .then((response) => {
        if (!isCancelled) {
          setBeingOptions(toArray(response));
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          setBeingOptions([]);
          setBeingLoadError(error?.message || 'Failed to load beings.');
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoadingBeings(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const loadSelectedBeings = async () => {
      setIsLoadingNpcContext(true);
      setNpcContextError('');

      try {
        const entries = await Promise.all(
          Object.entries(npcSelection).map(async ([key, idEntity]) => [
            key,
            idEntity ? await callCdo(cdoBeing.select.bind(cdoBeing), idEntity) : null,
          ])
        );

        if (!isCancelled) {
          setNpcDetails(Object.fromEntries(entries));
        }
      } catch (error) {
        if (!isCancelled) {
          setNpcDetails(emptyNpcDetails);
          setNpcContextError(error?.message || 'Failed to load selected being context.');
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingNpcContext(false);
        }
      }
    };

    loadSelectedBeings();

    return () => {
      isCancelled = true;
    };
  }, [npcSelection]);

  useEffect(() => {
    let isCancelled = false;

    if (!primaryFocusWorkplaceId) {
      setPrimaryFocusWorkplace(null);
      setIsLoadingWorkplace(false);
      return () => {
        isCancelled = true;
      };
    }

    setIsLoadingWorkplace(true);
    callCdo(cdoPlace.select.bind(cdoPlace), primaryFocusWorkplaceId)
      .then((response) => {
        if (!isCancelled) {
          setPrimaryFocusWorkplace(response || null);
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          setPrimaryFocusWorkplace(null);
          setNpcContextError(error?.message || 'Failed to load primary focus workplace.');
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoadingWorkplace(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [primaryFocusWorkplaceId]);

  useEffect(() => {
    let isCancelled = false;

    if (nearbyWorkplaceIds.length === 0) {
      setNearbyWorkplaces([]);
      setIsLoadingNearbyWorkplaces(false);
      return () => {
        isCancelled = true;
      };
    }

    setIsLoadingNearbyWorkplaces(true);
    Promise.all(nearbyWorkplaceIds.map((idEntity) =>
      callCdo(cdoPlace.select.bind(cdoPlace), idEntity)
    ))
      .then((responses) => {
        if (!isCancelled) {
          setNearbyWorkplaces(responses.filter(Boolean));
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          setNearbyWorkplaces([]);
          setNpcContextError(error?.message || 'Failed to load nearby target workplaces.');
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoadingNearbyWorkplaces(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [nearbyWorkplaceIds]);

  const updateNpcSelection = (key, value) => {
    setNpcSelection((current) => ({
      ...current,
      [key]: value,
    }));
    setNpcIntentResult(null);
    setNpcResponseGroups([]);
    setNpcError('');
  };

  const submitIntentText = async (event) => {
    event.preventDefault();

    const trimmedText = intentText.trim();
    if (!trimmedText) {
      setIntentError('Player text is required.');
      setIntentResult(null);
      return;
    }

    setIsSubmittingIntent(true);
    setIntentError('');

    try {
      const response = await callCdo(cdoAI.playerAction.bind(cdoAI), {
        text: trimmedText,
      });
      setIntentResult(response);
    } catch (requestError) {
      setIntentResult(null);
      setIntentError(requestError?.message || 'Failed to classify player text.');
    } finally {
      setIsSubmittingIntent(false);
    }
  };

  const loadNpcResponseEvents = async (playerAction) => {
    if (!getResultSocialContext(playerAction).includes('events')) {
      return [];
    }

    const targetIds = getResultTargetEntityIds(playerAction);
    const beingIds = targetIds.length > 0
      ? targetIds
      : [npcDetails.primaryFocus?.idEntity].filter(Boolean);
    const eventRequests = [
      ...beingIds.map((idEntity) =>
        callCdo(cdoEvent.selectMemoryForBeing.bind(cdoEvent), {
          idEntity,
          limit: 20,
        })
      ),
    ];

    if (primaryFocusWorkplace?.idEntity) {
      eventRequests.push(callCdo(cdoEvent.selectByPlace.bind(cdoEvent), {
        idPlace: primaryFocusWorkplace.idEntity,
        limit: 20,
      }));
    }

    const eventResults = await Promise.all(eventRequests);
    return uniqueEvents(eventResults.flatMap(toArray));
  };

  const loadNpcResponseRegionalPlaces = async (playerAction) => {
    if (!getResultSocialContext(playerAction).includes('regional_context')) {
      return [];
    }

    return toArray(await callCdo(cdoPlace.selectAll.bind(cdoPlace)));
  };

  const submitNpcText = async (event) => {
    event.preventDefault();

    const trimmedText = npcText.trim();
    if (!trimmedText) {
      setNpcError('Player text is required.');
      setNpcIntentResult(null);
      setNpcResponseGroups([]);
      return;
    }

    if (!npcSelection.pc || !npcSelection.primaryFocus) {
      setNpcError('Select a PC and Primary Focus before submitting NPC Response.');
      setNpcIntentResult(null);
      setNpcResponseGroups([]);
      return;
    }

    if (!npcDetails.pc || !npcDetails.primaryFocus || isLoadingNpcContext || isLoadingWorkplace || isLoadingNearbyWorkplaces) {
      setNpcError('Selected context is still loading.');
      return;
    }

    setIsSubmittingNpc(true);
    setNpcError('');
    setNpcResponseGroups([]);

    try {
      const nearbyTargets = [npcDetails.nearbyOne, npcDetails.nearbyTwo]
        .map(getSocialFrameTarget)
        .filter(Boolean);
      const playerAction = await callCdo(cdoAI.playerAction.bind(cdoAI), {
        text: trimmedText,
        socialFrame: {
          currentTarget: getSocialFrameTarget(npcDetails.primaryFocus),
          nearbyTargets,
        },
      });
      setNpcIntentResult(playerAction);

      const responseIntents = getNpcResponseIntents(playerAction);
      if (responseIntents.length === 0) {
        return;
      }

      const [events, regionalPlaces] = await Promise.all([
        loadNpcResponseEvents(playerAction),
        loadNpcResponseRegionalPlaces(playerAction),
      ]);
      const responseGroups = [];
      const basePayload = {
        text: trimmedText,
        playerAction,
        pc: npcDetails.pc,
        primaryFocus: npcDetails.primaryFocus,
        nearbyBeings: [npcDetails.nearbyOne, npcDetails.nearbyTwo].filter(Boolean),
        primaryFocusWorkplace,
        nearbyWorkplaces,
        events,
        regionalPlaces,
      };

      for (const intent of responseIntents) {
        const response = await callCdo(cdoAI.npcResponse.bind(cdoAI), {
          ...basePayload,
          intent,
        });
        responseGroups.push({
          intent,
          result: response,
        });
      }

      setNpcResponseGroups(responseGroups);
    } catch (requestError) {
      setNpcResponseGroups([]);
      setNpcError(requestError?.message || 'Failed to run NPC response.');
    } finally {
      setIsSubmittingNpc(false);
    }
  };

  const clearIntentText = () => {
    setIntentText('');
    setIntentResult(null);
    setIntentError('');
  };

  const clearNpcText = () => {
    setNpcText('');
    setNpcIntentResult(null);
    setNpcResponseGroups([]);
    setNpcError('');
  };

  const hasNpcResponseIntentResult = getNpcResponseIntents(npcIntentResult).length > 0;
  const workplaceItemCount = toArray(primaryFocusWorkplace?.containedItems).length;

  return (
    <div className="gameplay-play-sections">
      <section className="editor-section gameplay-npc-response-section">
        <h3>NPC Response</h3>
        <div className="editor-grid editor-grid-four gameplay-play-selector-grid">
          <BeingSelect
            id="gameplay-npc-pc"
            label="PC"
            value={npcSelection.pc}
            options={beingSelectOptions}
            disabled={isLoadingBeings || isSubmittingNpc}
            onChange={(value) => updateNpcSelection('pc', value)}
          />
          <BeingSelect
            id="gameplay-npc-primary-focus"
            label="Primary Focus"
            value={npcSelection.primaryFocus}
            options={beingSelectOptions}
            disabled={isLoadingBeings || isSubmittingNpc}
            onChange={(value) => updateNpcSelection('primaryFocus', value)}
          />
          <BeingSelect
            id="gameplay-npc-nearby-one"
            label="Nearby Being 1"
            value={npcSelection.nearbyOne}
            options={beingSelectOptions}
            disabled={isLoadingBeings || isSubmittingNpc}
            onChange={(value) => updateNpcSelection('nearbyOne', value)}
          />
          <BeingSelect
            id="gameplay-npc-nearby-two"
            label="Nearby Being 2"
            value={npcSelection.nearbyTwo}
            options={beingSelectOptions}
            disabled={isLoadingBeings || isSubmittingNpc}
            onChange={(value) => updateNpcSelection('nearbyTwo', value)}
          />
        </div>

        {beingLoadError && <div className="form-error">{beingLoadError}</div>}
        {npcContextError && <div className="form-error">{npcContextError}</div>}
        {(isLoadingNpcContext || isLoadingWorkplace) && (
          <div className="panel-copy">Loading selected context...</div>
        )}
        {primaryFocusWorkplace && (
          <div className="gameplay-play-context-summary">
            <span>Works At: {getEntityLabel(primaryFocusWorkplace)}</span>
            <span>Contained Items: {workplaceItemCount}</span>
          </div>
        )}

        <div className="gameplay-play-test-panel gameplay-play-npc-form-grid">
          <PlayerTextForm
            formId="gameplay-npc-response-text"
            text={npcText}
            setText={setNpcText}
            isSubmitting={isSubmittingNpc}
            submitLabel="Submit"
            onSubmit={submitNpcText}
            onClear={clearNpcText}
          />

          <div className="gameplay-play-result-panel">
            <h4>First Call</h4>
            <IntentResultList result={npcIntentResult} entityNameById={entityNameById} />
            {npcIntentResult && !hasNpcResponseIntentResult && (
              <div className="empty-grid">No NPC response intent returned.</div>
            )}
          </div>
        </div>

        {npcError && <div className="form-error">{npcError}</div>}
        <div className="gameplay-play-npc-results-panel">
          <h4>NPC Responses</h4>
          <NpcResponseResults
            groups={npcResponseGroups}
            entityNameById={entityNameById}
            itemNameById={itemNameById}
          />
        </div>
      </section>

      <section className="editor-section gameplay-intent-test-section">
        <h3>Intent Test</h3>
        <div className="gameplay-play-test-panel">
          <PlayerTextForm
            formId="gameplay-intent-test-text"
            text={intentText}
            setText={setIntentText}
            isSubmitting={isSubmittingIntent}
            submitLabel="Submit"
            onSubmit={submitIntentText}
            onClear={clearIntentText}
          />

          <div className="gameplay-play-result-panel">
            <h4>Intents</h4>
            <IntentResultList result={intentResult} />
          </div>
        </div>
        {intentError && <div className="form-error">{intentError}</div>}
      </section>
    </div>
  );
}

export default GameplayPlayTestPanel;
